/**
 * The vocabulary both halves of the site speak. Every enum-ish column in the
 * database has its allowed values here, and the zod schemas, the SQL CHECK
 * constraints and the UI all derive from these arrays.
 */

export const LOCALES = ["en", "fa"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

export const SUBMISSION_KINDS = ["text", "video", "audio", "link"] as const;
export type SubmissionKind = (typeof SUBMISSION_KINDS)[number];

export const IDENTITY_PREFERENCES = ["real_name", "pseudonym", "anonymous"] as const;
export type IdentityPreference = (typeof IDENTITY_PREFERENCES)[number];

/** Review workflow. `used` is terminal in the sense that consent records for it are never deleted. */
export const SUBMISSION_STATUSES = [
  "new",
  "reviewed",
  "shortlisted",
  "contacted",
  "used",
  "not_used",
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const MEDIA_KINDS = ["video", "audio"] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_STATUSES = ["processing", "ready", "rejected", "deleted"] as const;
export type MediaStatus = (typeof MEDIA_STATUSES)[number];

export const CONTACT_TOPICS = ["casting", "participation", "press", "other"] as const;
export type ContactTopic = (typeof CONTACT_TOPICS)[number];

export const CONTACT_STATUSES = ["new", "replied", "closed"] as const;
export type ContactStatus = (typeof CONTACT_STATUSES)[number];

export const CONTACT_LANGUAGES = ["fa", "en", "sv", "other"] as const;
export type ContactLanguage = (typeof CONTACT_LANGUAGES)[number];

export const AMOUNT_INTENTS = ["lt25", "25_100", "100_500", "gt500", "undisclosed"] as const;
export type AmountIntent = (typeof AMOUNT_INTENTS)[number];

export const NEWSLETTER_SOURCES = ["hero", "trailer", "footer", "wizard", "support"] as const;
export type NewsletterSource = (typeof NEWSLETTER_SOURCES)[number];

export const CLIP_FORMATS = ["video", "short"] as const;
export type ClipFormat = (typeof CLIP_FORMATS)[number];

/**
 * Every button on the ShareBar, plus the two destinations we cannot see into:
 * `webshare` (navigator.share — the OS sheet never tells us where it went) and
 * `copy_link`. Instagram and TikTok have no web share-intent, so their buttons
 * copy a caption and are recorded as such.
 */
export const SHARE_PLATFORMS = [
  "telegram",
  "whatsapp",
  "instagram_copy",
  "tiktok_copy",
  "x",
  "facebook",
  "bluesky",
  "linkedin",
  "reddit",
  "webshare",
  "copy_link",
] as const;
export type SharePlatform = (typeof SHARE_PLATFORMS)[number];

export const DELETION_REASONS = ["withdrawal", "erasure_request", "admin", "retention"] as const;
export type DeletionReason = (typeof DELETION_REASONS)[number];

/**
 * Whether the withdrawal link reached the contributor's inbox.
 *
 * Tri-state on purpose, and reported by the server rather than derived by the
 * client. The success screen says in words whether we emailed the link, and
 * that sentence has to be a measured fact: it used to be inferred from "did
 * they type an address", so when Postmark refused every message the screen
 * cheerfully told people to check an inbox that would never receive anything.
 * With three explicit states the client has nothing left to infer.
 */
export type WithdrawReceipt = "sent" | "failed" | "not_requested";

/** What the API hands back after a story lands. The token is shown exactly once. */
export interface SubmissionCreated {
  id: string;
  withdrawToken: string;
  receipt: WithdrawReceipt;
}

export interface MediaStatusResponse {
  status: MediaStatus;
  reason?: string;
  durationSeconds?: number;
}

export interface PublicClip {
  slug: string;
  youtubeId: string;
  format: ClipFormat;
  title: string;
  caption: string;
  description: string | null;
  thumbnailUrl: string;
  publishedAt: string | null;
}

export interface SiteStats {
  /** Stories accepted so far (spam-flagged and withdrawn excluded). */
  storyCount: number;
}
